import type { PlannedTargetWeight } from "./schemas/plan.js";
import { e1rmAtEffort, weightForE1rm } from "./pr.js";
import { isMaxTrustedForProposal, type MaxSource } from "./schemas/max.js";
import { isMaxStaleForProposal } from "./maxRegression.js";
import type { WeightSource } from "./schemas/weightSource.js";
import { roundTo } from "./units.js";

// Per-set weight provenance. Persisted on LogSet.weightSource so the
// post-session "did the athlete deviate from prescription" view, the
// percent-of-MAX delta math, and a future "re-prefill on resume" path
// all read a stable label. 'blank' is a transient signal from
// resolveDefaultWeight meaning "no prefill" and is never persisted.
//
// ⚠️ This block used to end "Data-only — no UI cue is rendered, by design
// (see next/archive/default-weights.md)". That stopped being true with the
// effort arm: a load the app DERIVED to reach a prescribed effort is a
// PROPOSAL, and §17 requires a proposal to carry where it came from. The
// three `effort-from-*` values are read by the logger's weight label; the
// other three stay data-only, and the archived note governs only those.

// An effort-axis prescription, as the chain needs to see it: the target the
// author wrote, already resolved to the canonical RIR scale (effortScale.ts),
// plus the reps the set asks for. ⛔ Both are needed — "RIR 2" alone does not
// name a load, "RIR 2 for 8" does.
export type EffortIntent = { targetRir: number; reps: number };

// The athlete's most recent weighted set on the exercise. Carries the REPS,
// not only the kg: without them the set cannot become an e1RM, and without an
// e1RM it cannot answer "what load reaches this effort?" — which is why the
// history arm of the effort chain could not exist before (measurement-contract.md §17).
// D9: and the RIR it was logged at (canonical, fail = 0), so its e1RM is the
// one at that effort. Absent or null: not recorded, the reps alone count.
export type LastLoggedSet = { weightKg: number; reps: number; rir?: number | null };

// A weight the CHAIN filled in (prescription / %MAX / history), as opposed
// to one the athlete typed ('manual') or touched-without-provenance
// (absent — equivalent to manual by LogSet convention). The save path omits
// non-prefilled sources; the swap path clears a prefilled weight because it
// described the OLD exercise's prescription (IB-8 follow-up: bench 40 kg
// carried onto a dumbbell press would read as 40 per implement = 80 total).
export const isPrefilledWeightSource = (
  source: WeightSource | undefined,
): source is Exclude<WeightSource, "manual"> =>
  source !== undefined && source !== "manual";

export type ResolvedDefaultWeight = {
  kg: number;
  source: WeightSource | "blank";
};

// ⛔ A candidate is never 'blank'. 'blank' is the ANSWER when the list is
// empty, not a member of it — typing the list as `ResolvedDefaultWeight[]`
// would make every reader handle a case that cannot occur, and the one that
// forgot would print "blank" as a provenance.
export type WeightCandidate = { kg: number; source: WeightSource };

// Kilo rounding for the chain, in ONE place. Deliberately 2 decimals and not
// `units.roundWeight(x, "kg", "storage")`'s gram precision: this is the
// granularity the percent-of-MAX
// branch has always produced, and the bodyweight subtraction in
// `buildPlanStarter` matched it by declaring its own copy. Exported so it
// stays one number — a second definition is how two "same" weights start
// disagreeing in the third decimal.
export const roundChainKg = (x: number) => roundTo(x, 2);

// THE EFFORT ARM — "carica la barra e fai un RIR 2. Ok, con che peso?"
// (Antenore 2026-08-26, PG-9). An ordered chain of INSTRUMENTS, never a
// formula: each one may DECLINE, and when they all decline the list comes
// back EMPTY — the caller leaves the cell blank rather than invent a target,
// exactly as the percent-with-no-MAX branch below already does.
//
// ⚡⚡ THE ORDER WAS REVERSED on 2026-09-02 (measurement-contract.md §17.5).
// It used to LEAD with the MAX, on the reading "no estimate beats a measure".
// Antenore's correction, and it is a statement about what this app IS:
//
//   «Metria è RPE based... c'è solo un modo per loggare un workout e sono
//    RPE/RIR o AMRAP che danno l'e1RM, quindi noi prima mostriamo quello,
//    sempre. Se non abbiamo quel dato perché qualcuno viene con i suoi dati
//    da un'altra APP, allora ci restano il max»
//
// ⇒ The MAX is not this house's instrument — it is the FALLBACK for someone
// arriving from outside. Every set logged here carries an effort, and an
// effort with its reps IS an e1RM: fresh, and the athlete's own. The old
// order kept that at position 3, behind a number that can be three months
// old. `PG-11` is what that cost: a `derived` MAX of 69 kg from 2026-05-28
// proposed 50 kg for an RPE 5×5 while his last real front squat, six days
// earlier, said 35.
//
// The order, and each link's reason:
//   1. the athlete's own last set, turned into an e1RM — freshest, and the
//      only instrument that needs no MAX at all.
//   2. a MAX still FRESH (`isMaxStaleForProposal`) and trusted by pedigree
//      (`isMaxTrustedForProposal`, which already refuses a `calculated_seed`
//      because 65 of Antenore's 115 maxes were seeds and one ran 51% high).
//   3. the raw last weight — blind to the effort, which is why it sits here
//      and not higher; it is a weight he really lifted, not an estimate.
//   4. the same MAX when it is STALE. ⛔ Demoted, never refused: it stays in
//      the panel so nothing the athlete used to see disappears.
//
// The arithmetic: "N reps at RIR r" reads as "N + r reps to failure", and the
// load comes back through `weightForE1rm`. Its validity band is Epley's own —
// past 12 reps-to-failure every instrument here declines, which is why a
// literal "RIR 9 for 8" (17 to failure) still ends blank. That is the answer
// Antenore chose on 2026-09-01 over showing a number with a caveat.

/** A MAX as the chain reads it. `CurrentMax` (maxRegression.ts) is assignable
 *  to this, so the surfaces that fetch the detail just hand theirs over. A
 *  surface that only has the kilos (the legacy web was one, until `RW-14`)
 *  still answers HONESTLY by omitting what it does not know: no
 *  source reads as untrusted, no date reads as stale. ⛔ Neither is defaulted:
 *  inventing a pedigree or a timestamp is the one thing this must not do. */
export type MaxForProposal = {
  valueKg: number;
  source?: MaxSource;
  setAt?: string;
};

/** Everything the chain needs, in ONE shape. It used to be spelled out twice —
 *  once on `listDefaultWeightCandidates` and once on `resolveDefaultWeight` —
 *  and a field added to one of them would have quietly not existed on the
 *  other. */
export type DefaultWeightContext = {
  /** Athlete's current MAX kg for the exercise (canonical kg). */
  userMax: number | undefined;
  /** Where that MAX came from. ⛔ Absent means UNKNOWN, which the effort
   *  chain treats as untrusted — never as trusted (the same reading
   *  `isMaxTrustedForProposal` already documents). */
  userMaxSource?: MaxSource;
  /** When that MAX was recorded (`Max.setAt`). ⛔ Absent means UNKNOWN, which
   *  reads as STALE — a caller that has not plumbed the date gets the
   *  cautious answer (link 4), not the confident one. */
  userMaxSetAt?: string;
  /** The athlete's last logged set on this exercise. Empty when they have
   *  no history for it yet. */
  lastLogged: LastLoggedSet | undefined;
  /** Present only when the prescription's axis IS the effort — i.e. the
   *  author asked for an RIR/RPE and left the load to the app. ⛔ Absent on
   *  a warmup or a mobility hold, which reach this function through the
   *  SAME `kind: 'none'` branch and must keep falling through untouched. */
  effort?: EffortIntent;
  /** The clock, injectable so the age gate is testable. Defaults to now. */
  nowMs?: number;
};

// "N reps at RIR r" ≈ "N + r reps to failure", or null when the set does not
// name an effort at all. Shared by both MAX and history links so the two can
// never disagree about what the target even is.
const repsToFailureOf = (effort: EffortIntent | undefined): number | null => {
  if (effort === undefined) return null;
  if (!Number.isFinite(effort.targetRir) || effort.targetRir < 0) return null;
  return effort.reps + effort.targetRir;
};

// LINK 1 — the athlete's own last set, through its e1RM. The anchor has to be
// Epley-eligible in its OWN right before it can be turned around: a 20-rep set
// is not evidence of a one-rep maximum, so it is not evidence of anything
// derived from one either. D9: the e1RM at the effort the set was logged
// with (`e1rmAtEffort`), the same reading the target gets below.
const effortFromHistory = (ctx: DefaultWeightContext): WeightCandidate | null => {
  const repsToFailure = repsToFailureOf(ctx.effort);
  if (repsToFailure === null) return null;
  const { lastLogged } = ctx;
  if (lastLogged === undefined) return null;
  const e1rm = e1rmAtEffort(lastLogged.weightKg, lastLogged.reps, lastLogged.rir ?? null);
  if (e1rm === null) return null;
  const kg = weightForE1rm(e1rm, repsToFailure);
  return kg === null
    ? null
    : { kg: roundChainKg(kg), source: "effort-from-history" };
};

// LINKS 2 and 4 — a MAX worked back to the target effort. At most ONE
// candidate, because an exercise has one MAX; the label is what separates a
// single the athlete really attempted from a value that was projected.
// ⛔ WHERE it sits is deliberately NOT decided here: that is the age gate, and
// it belongs to the list that knows about the other links.
const effortFromMax = (ctx: DefaultWeightContext): WeightCandidate | null => {
  const repsToFailure = repsToFailureOf(ctx.effort);
  if (repsToFailure === null) return null;
  const { userMax, userMaxSource } = ctx;
  if (userMax === undefined) return null;
  const kg = weightForE1rm(userMax, repsToFailure);
  if (kg === null) return null;
  if (userMaxSource === "tested") {
    return { kg: roundChainKg(kg), source: "effort-from-tested-max" };
  }
  if (isMaxTrustedForProposal(userMaxSource)) {
    return { kg: roundChainKg(kg), source: "effort-from-max" };
  }
  return null;
};

// LG-59 — EVERY instrument that can answer, best first, instead of only the
// winner. The history widget's first section shows them all with the one in
// use on top (`next/exercise-level-and-proposal.md` §6), and an athlete
// who can see that a tested 1RM and their last set disagree by 6 kg knows
// something a single number cannot tell them.
//
// ⛔ This is the SAME list `resolveDefaultWeight` walks — that function is
// defined as its first element below, and not as a second copy of the order.
// Two lists of candidates is exactly how the panel and the cell would start
// naming different instruments for the same weight.
//
// ⛔ And it enumerates INSIDE each branch, never across them: a percentage
// with no MAX still answers with nothing at all, because falling through to
// history would show a number the coach never prescribed (the 2026-06-01
// rule, restated at its branch below).
export const listDefaultWeightCandidates = (
  intent: PlannedTargetWeight,
  ctx: DefaultWeightContext,
): WeightCandidate[] => {
  if (intent.kind === "absolute") {
    return [{ kg: intent.weightKg, source: "prescribed-absolute" }];
  }
  if (intent.kind === "percentage") {
    // A percent-of-MAX prescription with no known MAX must NOT silently
    // borrow the athlete's last logged weight — that shows a number the
    // coach never prescribed (Antenore 2026-06-01: a gym-network blip
    // emptied the MAX map and benches prefilled off history). Nothing at
    // all, so the cell reads as "prescription pending a MAX".
    if (ctx.userMax === undefined) return [];
    return [
      {
        kg: roundChainKg((ctx.userMax * intent.pctOfMax) / 100),
        source: "computed-percent",
      },
    ];
  }
  const out: WeightCandidate[] = [];
  // ⭐ The effort arm hangs HERE and only under 'none', because 'none' is what
  // program resolution writes when the slot's intensity is `rpe`/`rir`: the
  // load is not absent, it is DELEGATED (§17 — "l'asse dice CHI determina il
  // carico, non che il carico non ci sia"). An explicit `kind: 'history'` is
  // the author choosing history AS the axis, and an effort target sitting
  // beside it does not get to overrule them.
  //
  // ⚡ §17.5 — the MAX no longer leads. It is held back here and placed by its
  // AGE: fresh, it goes above the raw history line; stale, below it. ⛔ Held,
  // not dropped — the panel lists every instrument, and an athlete who can see
  // that their three-month-old max and last week's set disagree by 15 kg knows
  // something neither number says alone.
  let staleMax: WeightCandidate | null = null;
  if (intent.kind === "none") {
    const fromHistory = effortFromHistory(ctx);
    if (fromHistory !== null) out.push(fromHistory);
    const fromMax = effortFromMax(ctx);
    if (fromMax !== null) {
      if (isMaxStaleForProposal(ctx.userMaxSetAt, ctx.nowMs ?? Date.now())) {
        staleMax = fromMax;
      } else {
        out.push(fromMax);
      }
    }
  }
  if (ctx.lastLogged !== undefined) {
    out.push({ kg: ctx.lastLogged.weightKg, source: "history" });
  }
  if (staleMax !== null) out.push(staleMax);
  return out;
};

// Single resolution chain used by buildPlanStarter (server-author and
// client-author flows alike) so mobile + web + post-session math agree
// on what number prefilled a set and why. Pure: bodyweight subtraction
// (extra = total − bw) is the caller's job since it depends on the
// catalog row, not on the intent.
export const resolveDefaultWeight = (
  intent: PlannedTargetWeight,
  ctx: DefaultWeightContext,
): ResolvedDefaultWeight => {
  // ⛔ THE FIRST OF THE LIST, never a second walk of the same order. The
  // ordered chain, its per-branch rules and the reason each instrument may
  // decline all live in `listDefaultWeightCandidates` above; an empty list is
  // the caller's "leave the cell blank" — a percentage with no MAX and an
  // effort target nothing can answer both land here.
  const [first] = listDefaultWeightCandidates(intent, ctx);
  return first ?? { kg: 0, source: "blank" };
};

// EQ-1-SWAP-PCTMAX — carry a weight from one exercise to another as the same
// FRACTION of the athlete's MAX. Antenore, 2026-08-20: *"quando l'esercizio ha
// un peso fisso, in teoria, il sistema potrebbe calcolare quanto è in
// percentuale del MAX e convertire il peso dell'attrezzo alternativo di
// conseguenza se un MAX è registrato."*
//
// It handles his two cases with one rule. A percent-of-MAX prescription
// converts exactly (the fraction is the prescription). A fixed prescribed
// weight converts by the fraction it implicitly represents. Neither needs the
// plan intent, which is what makes this reachable at all: `buildPlanStarter`
// resolves the intent to kg and does NOT carry it onto PlanStarter, so the
// resolved kg is the only thing a mid-session swap still has in hand.
//
// ⛔ The TRUST of the two MAXes is the caller's gate (isMaxTrustedForProposal),
// not this function's: this one is arithmetic, and folding a policy into it
// would hide the refusal in a place nobody reviewing "the conversion" would
// look. ⛔ Bodyweight exercises are out of scope for the same reason they are
// special in buildPlanStarter — a MAX there is TOTAL load and the logger shows
// the EXTRA, so the ratio would silently compare two different quantities.
// The caller refuses those too.
//
// ⛔ Rounding is NOT done here. A ratio produces un-loadable numbers (68.38 kg)
// and the floor depends on the athlete's plate inventory, which lives in the
// UI layer; `roundLoadable` is the one place that knows how. Returning the raw
// kg keeps this pure and keeps one rounding implementation in the codebase.
//
// Returns null when the conversion cannot be made, which the caller reads as
// "leave the cell blank" — the same answer resolveDefaultWeight gives a
// percent with no MAX, and for the same reason: an invented number is worse
// than an empty cell.
export const convertWeightByMaxRatio = (params: {
  fromWeightKg: number | undefined;
  fromMaxKg: number | undefined;
  toMaxKg: number | undefined;
}): number | null => {
  const { fromWeightKg, fromMaxKg, toMaxKg } = params;
  if (
    fromWeightKg === undefined ||
    fromMaxKg === undefined ||
    toMaxKg === undefined
  ) {
    return null;
  }
  if (
    !Number.isFinite(fromWeightKg) ||
    !Number.isFinite(fromMaxKg) ||
    !Number.isFinite(toMaxKg)
  ) {
    return null;
  }
  // A non-positive MAX is not a small MAX, it is a missing one: `cheese-
  // sandwich` really does sit at 0 kg in production, and dividing by it would
  // hand back Infinity dressed as a training weight.
  if (fromWeightKg <= 0 || fromMaxKg <= 0 || toMaxKg <= 0) return null;
  return (fromWeightKg / fromMaxKg) * toMaxKg;
};
